import { useAssetActions } from 'asset-actions-lib/context/AssetActionsContext';
import { contentIsPublished, getContentPropertyValue, useContentObjectType } from 'content-management-data-lib';
import { CONTENT_PLATFORM_PROPERTY_NAME } from 'content-management-data-lib';
import useFetchContentPrerenderingProgress from 'ContentComponents/hooks/useFetchContentPrerenderingProgress';
import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import I18n from 'I18n';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { gate } from 'hub-http/gates';
import { Button } from 'foundations-components/transitional/components/Button';
import { jsx as _jsx } from "react/jsx-runtime";
const FREE_DOMAIN_REGEX = /^.+\.hubspotpagebuilder(qa)?(-[A-z0-9]+)?\..+/;
const DATA_TEST_ID = 'page-live-url';
const VIEW_LABEL_KEY = 'content-management-actions-lib.actions.view';
const PRERENDERING_TIME_THRESHOLD_MS = 120000; // 2 minutes

const stripProtocol = url => url.replace(/^(http:\/\/)|(https:\/\/)/, '');
const getUrl = crmObject => {
  const url = getContentPropertyValue(crmObject, CONTENT_PLATFORM_PROPERTY_NAME.ABSOLUTE_URL);
  if (!url) {
    return undefined;
  }
  const previewKey = getContentPropertyValue(crmObject, CONTENT_PLATFORM_PROPERTY_NAME.PREVIEW_KEY);
  if (!previewKey || contentIsPublished(crmObject)) {
    return url;
  }
  return `https://${stripProtocol(url)}?hs_preview=${previewKey}-${crmObject.objectId}`;
};

/**
 * Determines if content prerendering progress should be polled based on
 * publication time and domain type
 */
const getShouldPollForContentPrerendingProgress = ({
  contentObject,
  publishDate,
  gates,
  isContentOnFreeTier,
  scopes
}) => {
  if (!contentObject || !contentIsPublished(contentObject) || !publishDate || I18n.moment().valueOf() - publishDate > PRERENDERING_TIME_THRESHOLD_MS) {
    return false;
  }
  const domain = getContentPropertyValue(contentObject, CONTENT_PLATFORM_PROPERTY_NAME.RESOLVED_DOMAIN);
  const hasDynamicPublicContentAccess = scopes.includes('dynamic-public-content');
  const isUngatedForDynamicPublicContentScopeCheck = gates.includes(gate('CMSPublishing:DynamicPublicContentScopeCheck')) && gates.includes(gate('Domains::InternalDomainsWithHsSitesOnly'));

  // Check if domain is on free page builder
  const isDomainOnFreePageBuilder = domain ? new RegExp(FREE_DOMAIN_REGEX).test(domain) : false;

  // Check if content is prerendered based on domain and permissions
  return isDomainOnFreePageBuilder || (isUngatedForDynamicPublicContentScopeCheck ? !hasDynamicPublicContentAccess : isContentOnFreeTier);
};

/**
 * Component to render a URL link Button
 */
const UrlButton = ({
  href
}) => {
  return /*#__PURE__*/_jsx(Button, {
    "data-test-id": DATA_TEST_ID,
    external: false,
    href: href,
    use: "link",
    children: /*#__PURE__*/_jsx(FormattedMessage, {
      message: VIEW_LABEL_KEY
    })
  });
};

/**
 * Component to render a URL with a loading spinner while content prerender status is being polled
 */
const UrlWithLoadingSpinner = ({
  id,
  publishDate,
  url
}) => {
  const {
    contentObjectType
  } = useContentObjectType();
  const {
    isPolling,
    hasFinishedPrerendering
  } = useFetchContentPrerenderingProgress({
    contentType: contentObjectType,
    id,
    publishDate,
    url
  });
  if (!isPolling && !hasFinishedPrerendering) {
    return /*#__PURE__*/_jsx(UrlButton, {
      href: url
    });
  }
  const viewLabel = I18n.text(VIEW_LABEL_KEY);
  return /*#__PURE__*/_jsx(Button, {
    "data-test-id": DATA_TEST_ID,
    disabled: isPolling && !hasFinishedPrerendering,
    href: url,
    use: "link",
    children: /*#__PURE__*/_jsx(LoadingSpinner, {
      layout: "inline",
      marginInlineStart: "0",
      showResult: hasFinishedPrerendering,
      size: "xs",
      label: viewLabel,
      labelCompletedText: viewLabel,
      showLabel: true
    })
  });
};
/**
 * Button component that shows view action with prerendering status indicator
 */
export const ContentPrerenderViewActionButton = ({
  isContentOnFreeTier,
  gates,
  scopes
}) => {
  const {
    crmObject
  } = useAssetActions();
  if (!crmObject) {
    return null;
  }
  const url = getUrl(crmObject);
  if (!url) {
    return null;
  }
  const publishDate = getContentPropertyValue(crmObject, CONTENT_PLATFORM_PROPERTY_NAME.PUBLISH_DATE);
  const shouldPollForContentPrerenderingStatus = getShouldPollForContentPrerendingProgress({
    contentObject: crmObject,
    publishDate,
    gates,
    scopes,
    isContentOnFreeTier
  });
  if (!shouldPollForContentPrerenderingStatus) {
    return /*#__PURE__*/_jsx(UrlButton, {
      href: url
    });
  }
  return /*#__PURE__*/_jsx(UrlWithLoadingSpinner, {
    id: parseInt(crmObject.objectId, 10),
    publishDate: publishDate,
    url: url
  });
};